import { createContext, useContext } from "react";
import { useGetAiReportQuery } from "@/api/reports/reportApi";
import type {
  ActionsReportData,
  AnswersReportData,
  TranscribeReportData,
  AiReportData,
} from "@/types/endpoints/reports/GetAiReportById";

export type ReportType = 'actions' | 'answers' | 'transcribe' | 'q_density' | 'evaluation';

type ReportDataMap = {
  actions: ActionsReportData;
  answers: AnswersReportData;
  transcribe: TranscribeReportData;
  q_density: AiReportData;
  evaluation: AiReportData;
};

/**
 * Контекст данных AI-отчётов из SSE-пайплайна.
 * Когда провайдер присутствует (менеджерский ReportTab) — данные берутся отсюда,
 * а прямые RTK-запросы (`useGetAiReportQuery`) НЕ применяются (skip).
 * Когда контекста нет (например, публичный отчёт) — `useReportData` падает на RTK.
 */
export interface PipelineReportsContextValue {
  reports: Partial<Record<ReportType | "environment", unknown>>;
  isGenerating: boolean;
}

export const PipelineReportsContext =
  createContext<PipelineReportsContextValue | null>(null);

export function useReportData<T extends keyof ReportDataMap>(
  attemptId: string | number,
  reportType: T,
  options?: { skip?: boolean },
): { data?: ReportDataMap[T]; isLoading: boolean } {
  const numericAttemptId = typeof attemptId === 'string' ? parseInt(attemptId, 10) : attemptId;

  const pipelineCtx = useContext(PipelineReportsContext);
  const usePipeline = pipelineCtx !== null;

  // RTK-запрос оставлен в коде, но НЕ применяется, когда данные приходят из пайплайна.
  const query = useGetAiReportQuery(
    {
      attemptId: numericAttemptId,
      reportType,
    },
    {
      skip: options?.skip || usePipeline,
    },
  );

  if (usePipeline) {
    const data = pipelineCtx!.reports[reportType] as ReportDataMap[T] | undefined;
    return {
      data,
      isLoading: pipelineCtx!.isGenerating && data === undefined,
    };
  }

  return {
    data: query.data as ReportDataMap[T] | undefined,
    isLoading: query.isLoading,
  };
}
